import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { translations } from './translations';
import type { Language, Translations } from './translations';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const params = useParams<{ lang?: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Determine language from URL first, then fallback to localStorage/browser
  const getInitialLanguage = (): Language => {
    // Priority 1: URL parameter
    const urlLang = params.lang;
    if (urlLang === 'fr' || urlLang === 'ar') {
      return urlLang;
    }

    // Priority 2: localStorage
    const stored = localStorage.getItem('pravos-language');
    if (stored === 'en' || stored === 'fr' || stored === 'ar') {
      return stored;
    }

    // Priority 3: Browser language
    const browserLang = navigator.language.toLowerCase();
    if (browserLang.startsWith('fr')) return 'fr';
    if (browserLang.startsWith('ar')) return 'ar';

    // Default: English
    return 'en';
  };

  const [language, setLanguageState] = useState<Language>(getInitialLanguage);

  // Sync language state when URL changes
  useEffect(() => {
    const urlLang = params.lang;
    if (urlLang === 'fr' || urlLang === 'ar') {
      setLanguageState(urlLang);
    } else if (!urlLang) {
      setLanguageState('en');
    }
  }, [params.lang]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('pravos-language', lang);

    // Navigate to the correct URL path
    const currentPath = location.pathname;
    const currentPathWithoutLang = currentPath.replace(/^\/(fr|ar)/, '');

    let newPath: string;
    if (lang === 'en') {
      // English: remove language prefix
      newPath = currentPathWithoutLang || '/';
    } else {
      // French/Arabic: add language prefix
      newPath = `/${lang}${currentPathWithoutLang || ''}`;
    }

    navigate(newPath, { replace: true });
  };

  // Update HTML attributes when language changes
  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';

    if (language === 'ar') {
      document.body.classList.add('font-arabic');
    } else {
      document.body.classList.remove('font-arabic');
    }
  }, [language]);

  const value: LanguageContextType = {
    language,
    setLanguage,
    t: translations[language],
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
